/**
 * Sección **Precios** de *Control de Precios*.
 *
 * La tabla del día de la ubicación elegida, con su propio filtro de orden y la
 * acción de marcar cada estación: **completa** (todos sus productos de una vez) o
 * sólo el producto de esa fila.
 *
 * La tabla se queda con su propio desplazamiento (algo más de la mitad de la
 * pantalla)
 * para que el conjunto —barra de filtros fija, secciones y tabla— entre sin
desplazar la página; su cabecera es fija dentro del recuadro.
 */

import { ArrowDownUp, Building2, EyeOff, Target } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import type { CompetidorPricing, PrecioPricing } from '../pricing.types';
import { SeguimientoAcciones } from './seguimiento-acciones';
import { claveCompetidor, mensajeDe, normalizar } from '../pricing-utils';

type Orden = 'precio-asc' | 'precio-desc' | 'estacion';

const ORDENES: { clave: Orden; etiqueta: string }[] = [
  { clave: 'precio-asc', etiqueta: 'Precio ↑' },
  { clave: 'precio-desc', etiqueta: 'Precio ↓' },
  { clave: 'estacion', etiqueta: 'Estación A-Z' },
];

interface PreciosTablaProps {
  precios: PrecioPricing[];
  total: number;
  ultimaFecha?: string;
  cargando: boolean;
  error: unknown;
  /** Nombre legible de la ubicación (`LIMA / BARRANCA / BARRANCA`). */
  ubicacion: string;
  estacionesMarcadas: Set<string>;
  /** Competidor de cada `ESTACION|PRODUCTO` en seguimiento (para poder quitarlo). */
  porProducto: Map<string, CompetidorPricing>;
  ocupado: boolean;
  onMarcarEstacion: (estacion: string, precio?: number) => void;
  onQuitarEstacion: (estacion: string) => void;
  onMarcarProducto: (datos: {
    estacion: string;
    producto: string;
    direccion?: string;
    precio?: number;
  }) => void;
  onQuitarProducto: (competidor: CompetidorPricing) => void;
}

export function PreciosTabla({
  precios,
  total,
  ultimaFecha,
  cargando,
  error,
  ubicacion,
  estacionesMarcadas,
  porProducto,
  ocupado,
  onMarcarEstacion,
  onQuitarEstacion,
  onMarcarProducto,
  onQuitarProducto,
}: PreciosTablaProps) {
  const [orden, setOrden] = useState<Orden>('precio-asc');
  const [soloSinMarcar, setSoloSinMarcar] = useState(false);

  const filas = useMemo(() => {
    const filtradas = precios.filter((fila) => {
      if (!soloSinMarcar) return true;

      return (
        !estacionesMarcadas.has(normalizar(fila.estacion)) &&
        !porProducto.has(claveCompetidor(fila.estacion, fila.producto))
      );
    });

    const ordenadas = [...filtradas];

    if (orden === 'precio-asc') ordenadas.sort((a, b) => (a.precio ?? 0) - (b.precio ?? 0));
    else if (orden === 'precio-desc') ordenadas.sort((a, b) => (b.precio ?? 0) - (a.precio ?? 0));
    else ordenadas.sort((a, b) => a.estacion.localeCompare(b.estacion));

    return ordenadas;
  }, [estacionesMarcadas, orden, porProducto, precios, soloSinMarcar]);

  if (cargando) return <Skeleton className="h-72 w-full" />;

  if (error) {
    return (
      <Alert variant="info" title="Sin datos para esa fecha">
        <span className={ICONO_EN_ALERTA}>
          <EyeOff className="size-4" aria-hidden="true" />
          {mensajeDe(error, 'Esa fecha no tiene fichero para la ubicación elegida.')}
        </span>
      </Alert>
    );
  }

  if (!ubicacion) {
    return (
      <Card>
        <CardContent>
          <p className="text-sm text-ink-500">Elige una ubicación para ver sus precios.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-3">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-ink-900">Precios de {ubicacion}</h2>
            <p className="text-xs text-ink-400">
              {formatNumber(total)} precios con los filtros actuales
              {ultimaFecha ? ` · última fecha disponible: ${ultimaFecha}` : ''}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs text-ink-400">
              <ArrowDownUp className="size-3.5" aria-hidden="true" />
              Orden
            </span>
            {ORDENES.map((item) => (
              <Button
                key={item.clave}
                size="sm"
                variant={orden === item.clave ? 'primary' : 'secondary'}
                onClick={() => setOrden(item.clave)}
              >
                {item.etiqueta}
              </Button>
            ))}
            <Button
              size="sm"
              variant={soloSinMarcar ? 'primary' : 'secondary'}
              onClick={() => setSoloSinMarcar((previo) => !previo)}
            >
              Sólo sin marcar
            </Button>
          </div>
        </header>

        {filas.length === 0 ? (
          <p className="text-sm text-ink-500">Ningún precio coincide con el filtro actual.</p>
        ) : (
          <div className="max-h-[52vh] overflow-auto rounded-control border border-ink-50">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                  <th className="py-2 pl-3">Estación</th>
                  <th className="py-2">Producto</th>
                  <th className="py-2">Categoría</th>
                  <th className="py-2 text-right">Precio</th>
                  <th className="w-32 py-2 pr-3 text-right">Seguimiento</th>
                </tr>
              </thead>
              <tbody>
                {filas.slice(0, 300).map((fila, indice) => {
                  const competidor = porProducto.get(
                    claveCompetidor(fila.estacion, fila.producto),
                  );
                  const estacionMarcada = estacionesMarcadas.has(normalizar(fila.estacion));
                  const productoMarcado = Boolean(competidor);

                  return (
                    <tr
                      key={`${fila.estacion}-${fila.producto}-${indice}`}
                      className={cn(
                        'border-b border-ink-50 last:border-0',
                        (estacionMarcada || productoMarcado) && 'bg-emerald-50/40',
                      )}
                    >
                      <td className="py-2 pl-3 text-ink-700">{fila.estacion}</td>
                      <td className="py-2 text-ink-500">{fila.producto}</td>
                      <td className="py-2 text-ink-400">{fila.categoria}</td>
                      <td className="py-2 text-right font-medium text-ink-900">
                        {formatCurrency(fila.precio)}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        <SeguimientoAcciones
                          estacion={fila.estacion}
                          producto={fila.producto}
                          estacionMarcada={estacionMarcada}
                          productoMarcado={productoMarcado}
                          ocupado={ocupado}
                          onMarcarEstacion={() => onMarcarEstacion(fila.estacion, fila.precio)}
                          onQuitarEstacion={() => onQuitarEstacion(fila.estacion)}
                          onMarcarProducto={() =>
                            onMarcarProducto({
                              estacion: fila.estacion,
                              producto: fila.producto,
                              direccion: fila.direccion,
                              precio: fila.precio,
                            })
                          }
                          onQuitarProducto={() => competidor && onQuitarProducto(competidor)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-ink-400">
          {formatNumber(filas.length)} de {formatNumber(total)} precios mostrados
          {filas.length > 300 ? ' (se muestran los 300 primeros; afina con la búsqueda)' : ''} ·{' '}
          <Building2 className="inline size-3 -translate-y-px" aria-hidden="true" /> marca la
          estación completa, <Target className="inline size-3 -translate-y-px" aria-hidden="true" />{' '}
          el producto de la fila y la papelera quita lo que ya está en seguimiento (las filas en
          verde).
        </p>
      </CardContent>
    </Card>
  );
}
